import React, { useState, useEffect } from 'react';
import { 
  FileText, Copy, Check, Download, Printer, ExternalLink, 
  BookOpen, ChevronRight, BarChart2, TrendingUp, Info, 
  ShieldCheck, AlertTriangle, Lightbulb, ShieldAlert, ArrowLeft,
  Mic, Send, Users, RefreshCw, X, Radio, Star
} from 'lucide-react';
import { ResearchReport, ResearchSession } from '../types';
import { autoDownloadReport } from '../utils/autoDownloader';
import TelegramExportButton from './TelegramExportButton';
import ExcelExportButton from './ExcelExportButton';
import { DigitalSignatureModal } from './DigitalSignatureModal';

interface ReportViewProps {
  session: ResearchSession;
  onNewResearch: () => void;
  onBackToHub: () => void;
}

export default function ReportView({ session, onNewResearch, onBackToHub }: ReportViewProps) {
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<'content' | 'data' | 'swot' | 'charts'>('content');

  // 2026 Innovation States
  const [audioLoading, setAudioLoading] = useState(false);
  const [telegramSending, setTelegramSending] = useState(false);
  const [debateLoading, setDebateLoading] = useState(false);
  const [audioModalData, setAudioModalData] = useState<any>(null);
  const [debateModalData, setDebateModalData] = useState<any>(null);
  const [showSignModal, setShowSignModal] = useState(false);
  const [signedStampText, setSignedStampText] = useState<string | null>(null);
  const [savingGold, setSavingGold] = useState(false);
  const [goldSaved, setGoldSaved] = useState(false);

  const report = session?.report;
  const lang = session?.options?.language || 'uk';

  useEffect(() => {
    if (report) {
      let markdown = `# ${report.title}\n\n`;
      markdown += `## Executive Summary / Анотація\n\n${report.executiveSummary}\n\n`;
      
      report.sections.forEach(sec => {
        markdown += `## ${sec.title}\n\n${sec.content}\n\n`;
        sec.subsections?.forEach(subsec => {
          markdown += `### ${subsec.title}\n\n${subsec.content}\n\n`;
        });
      });

      autoDownloadReport(markdown, report.title || 'Глибоке Дослідження Deep Research', session?.id || 'deep-research');
    }
  }, [session?.id, report]);

  if (!report) return null;

  const handleCopyMarkdown = () => {
    let markdown = `# ${report.title}\n\n`;
    markdown += `## Executive Summary / Анотація\n\n${report.executiveSummary}\n\n`;
    
    report.sections.forEach(sec => {
      markdown += `## ${sec.title}\n\n${sec.content}\n\n`;
      sec.subsections?.forEach(subsec => {
        markdown += `### ${subsec.title}\n\n${subsec.content}\n\n`;
      });
    });

    markdown += `## Key Metrics & Data Points / Ключові показники\n\n`;
    report.dataPoints.forEach(dp => {
      markdown += `- **${dp.metric}**: ${dp.value} — ${dp.context}\n`;
    });
    markdown += `\n`;

    if (report.swotAnalysis) {
      markdown += `## SWOT Analysis / SWOT Аналіз\n\n`;
      markdown += `### Strengths / Сильні сторони\n${report.swotAnalysis.strengths.map(s => `- ${s}`).join('\n')}\n\n`;
      markdown += `### Weaknesses / Слабкі сторони\n${report.swotAnalysis.weaknesses.map(w => `- ${w}`).join('\n')}\n\n`;
      markdown += `### Opportunities / Можливості\n${report.swotAnalysis.opportunities.map(o => `- ${o}`).join('\n')}\n\n`;
      markdown += `### Threats / Загрози\n${report.swotAnalysis.threats.map(t => `- ${t}`).join('\n')}\n\n`;
    }

    markdown += `## Conclusion / Висновок\n\n${report.conclusion}\n\n`;
    markdown += `## Sources & Grounding / Список джерел\n\n`;
    report.sources.forEach(src => {
      markdown += `- [${src.title}](${src.url}) (${src.domain})\n`;
    });

    navigator.clipboard.writeText(markdown);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadFile = () => {
    let markdown = `# ${report.title}\n\n`;
    markdown += `## Executive Summary / Анотація\n\n${report.executiveSummary}\n\n`;
    
    report.sections.forEach((sec, sIdx) => {
      markdown += `## 1.${sIdx + 1}. ${sec.title}\n\n${sec.content}\n\n`;
      sec.subsections?.forEach((subsec, subIdx) => {
        markdown += `### 1.${sIdx + 1}.${subIdx + 1}. ${subsec.title}\n\n${subsec.content}\n\n`;
      });
    });

    markdown += `## 2. Key Extracted Metrics / Ключові показники\n\n`;
    report.dataPoints.forEach(dp => {
      markdown += `- **${dp.metric}**: ${dp.value} — ${dp.context}\n`;
    });
    markdown += `\n`;

    if (report.swotAnalysis) {
      markdown += `## 3. SWOT Analysis / SWOT Аналіз\n\n`;
      markdown += `### Strengths / Сильні сторони\n${report.swotAnalysis.strengths.map(s => `- ${s}`).join('\n')}\n\n`;
      markdown += `### Weaknesses / Слабкі сторони\n${report.swotAnalysis.weaknesses.map(w => `- ${w}`).join('\n')}\n\n`;
      markdown += `### Opportunities / Можливості\n${report.swotAnalysis.opportunities.map(o => `- ${o}`).join('\n')}\n\n`;
      markdown += `### Threats / Загрози\n${report.swotAnalysis.threats.map(t => `- ${t}`).join('\n')}\n\n`;
    }

    if (report.charts && report.charts.length > 0) {
      markdown += `## 4. Statistical Charts & Trends / Статистичні показники\n\n`;
      report.charts.forEach(c => {
        markdown += `- **${c.label}**: ${c.value} ${c.unit || ''}\n`;
      });
      markdown += `\n`;
    }

    markdown += `## 5. Conclusion / Висновок\n\n${report.conclusion}\n\n`;
    markdown += `## 6. Sources & Verification / Джерела та верифікація\n\n`;
    report.sources.forEach((src, idx) => {
      markdown += `${idx + 1}. [${src.title}](${src.url}) (${src.domain})\n`;
    });

    const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `deep-research-${session.id}.md`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadWord = () => {
    const title = report.title;
    const dateStr = new Date(session.createdAt).toLocaleDateString();
    const sector = session.options.focus.toUpperCase();
    const sourcesCount = report.sources.length;

    let html = `
      <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
      <head>
        <meta charset="utf-8">
        <title>${title}</title>
        <style>
          body {
            font-family: "Segoe UI", Arial, sans-serif;
            line-height: 1.6;
            color: #111827;
            margin: 40px;
          }
          h1 {
            font-family: Georgia, serif;
            color: #0369a1;
            font-size: 24pt;
            border-bottom: 2px solid #0369a1;
            padding-bottom: 8px;
            margin-top: 0;
            margin-bottom: 12px;
          }
          .meta {
            font-size: 10pt;
            color: #6b7280;
            margin-bottom: 24px;
            font-family: monospace;
          }
          h2 {
            font-family: Georgia, serif;
            color: #0f172a;
            font-size: 16pt;
            border-bottom: 1px solid #e2e8f0;
            padding-bottom: 6px;
            margin-top: 30px;
            margin-bottom: 12px;
          }
          h3 {
            color: #0369a1;
            font-size: 13pt;
            margin-top: 20px;
            margin-bottom: 8px;
          }
          p {
            font-size: 11pt;
            margin-bottom: 12px;
            text-align: justify;
          }
          .summary-box {
            background-color: #f0f9ff;
            border-left: 4px solid #0284c7;
            padding: 16px;
            margin-bottom: 24px;
            font-style: italic;
          }
          .summary-title {
            font-weight: bold;
            color: #0369a1;
            margin-bottom: 6px;
            font-style: normal;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin: 20px 0;
            font-size: 10pt;
          }
          th {
            background-color: #0f172a;
            color: #ffffff;
            font-weight: bold;
            text-align: left;
            padding: 10px;
            border: 1px solid #cbd5e1;
          }
          td {
            padding: 10px;
            border: 1px solid #cbd5e1;
            vertical-align: top;
          }
          tr:nth-child(even) {
            background-color: #f8fafc;
          }
          .swot-title-s { color: #0891b2; font-weight: bold; }
          .swot-title-w { color: #e11d48; font-weight: bold; }
          .swot-title-o { color: #2563eb; font-weight: bold; }
          .swot-title-t { color: #d97706; font-weight: bold; }
          .source-link {
            color: #0284c7;
            text-decoration: none;
          }
        </style>
      </head>
      <body>
        <h1>${title}</h1>
        <div class="meta">
          DEEP RESEARCH REPORT &bull; ${dateStr} &bull; SECTOR: ${sector} &bull; SOURCES: ${sourcesCount}
        </div>

        <div class="summary-box">
          <div class="summary-title">${lang === 'uk' ? 'АНОТАЦІЯ / EXECUTIVE SUMMARY' : 'EXECUTIVE SUMMARY'}</div>
          <p>${report.executiveSummary.replace(/\n/g, '<br>')}</p>
        </div>

        <h2>${lang === 'uk' ? '1. АНАЛІТИЧНИЙ ЗВІТ' : '1. DETAILED NARRATIVE ANALYSIS'}</h2>
    `;

    report.sections.forEach((sec, idx) => {
      html += `
        <h3>1.${idx + 1}. ${sec.title}</h3>
        <p>${sec.content.replace(/\n/g, '<br>')}</p>
      `;
      sec.subsections?.forEach((sub, subIdx) => {
        html += `
          <h4>1.${idx + 1}.${subIdx + 1}. ${sub.title}</h4>
          <p>${sub.content.replace(/\n/g, '<br>')}</p>
        `;
      });
    });

    html += `<h2>${lang === 'uk' ? '2. КЛЮЧОВІ ПОКАЗНИКИ / KEY METRICS' : '2. KEY EXTRACTED METRICS'}</h2>`;
    html += `
      <table>
        <thead>
          <tr>
            <th style="width: 30%;">${lang === 'uk' ? 'Показник / Метрика' : 'Metric / Parameter'}</th>
            <th style="width: 20%;">${lang === 'uk' ? 'Значення' : 'Value'}</th>
            <th style="width: 50%;">${lang === 'uk' ? 'Контекст та обґрунтування' : 'Context & Grounding'}</th>
          </tr>
        </thead>
        <tbody>
    `;

    report.dataPoints.forEach(dp => {
      html += `
        <tr>
          <td><strong>${dp.metric}</strong></td>
          <td><span style="color: #0284c7; font-weight: bold;">${dp.value}</span></td>
          <td>${dp.context}</td>
        </tr>
      `;
    });
    html += `</tbody></table>`;

    if (report.swotAnalysis) {
      html += `<h2>${lang === 'uk' ? '3. СТРАТЕГІЧНИЙ SWOT-АНАЛІЗ' : '3. SWOT STRATEGIC ANALYSIS'}</h2>`;
      html += `
        <table>
          <thead>
            <tr>
              <th class="swot-title-s" style="background-color: #ecfeff; color: #0891b2; width: 50%;">${lang === 'uk' ? 'Сильні сторони (Strengths)' : 'Strengths'}</th>
              <th class="swot-title-w" style="background-color: #fff1f2; color: #e11d48; width: 50%;">${lang === 'uk' ? 'Слабкі сторони (Weaknesses)' : 'Weaknesses'}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <ul>
                  ${report.swotAnalysis.strengths.map(s => `<li>${s}</li>`).join('')}
                </ul>
              </td>
              <td>
                <ul>
                  ${report.swotAnalysis.weaknesses.map(w => `<li>${w}</li>`).join('')}
                </ul>
              </td>
            </tr>
            <tr>
              <th class="swot-title-o" style="background-color: #eff6ff; color: #2563eb;">${lang === 'uk' ? 'Можливості (Opportunities)' : 'Opportunities'}</th>
              <th class="swot-title-t" style="background-color: #fffbeb; color: #d97706;">${lang === 'uk' ? 'Загрози (Threats)' : 'Threats'}</th>
            </tr>
            <tr>
              <td>
                <ul>
                  ${report.swotAnalysis.opportunities.map(o => `<li>${o}</li>`).join('')}
                </ul>
              </td>
              <td>
                <ul>
                  ${report.swotAnalysis.threats.map(t => `<li>${t}</li>`).join('')}
                </ul>
              </td>
            </tr>
          </tbody>
        </table>
      `;
    }

    if (report.charts && report.charts.length > 0) {
      html += `<h2>${lang === 'uk' ? '4. СТАТИСТИЧНІ ДІАГРАМИ ТА ТРЕНДИ' : '4. STATISTICAL TRENDS & CHART DATA'}</h2>`;
      html += `
        <table>
          <thead>
            <tr>
              <th>${lang === 'uk' ? 'Показник' : 'Label'}</th>
              <th>${lang === 'uk' ? 'Значення' : 'Value'}</th>
              <th>${lang === 'uk' ? 'Одиниця виміру' : 'Unit'}</th>
            </tr>
          </thead>
          <tbody>
      `;
      report.charts.forEach(c => {
        html += `
          <tr>
            <td>${c.label}</td>
            <td style="font-weight: bold; color: #0284c7;">${c.value}</td>
            <td>${c.unit || '-'}</td>
          </tr>
        `;
      });
      html += `</tbody></table>`;
    }

    html += `
      <h2>${lang === 'uk' ? '5. ВИСНОВКИ ТА РЕКОМЕНДАЦІЇ' : '5. CONCLUSIONS & PROSPECTUS'}</h2>
      <p>${report.conclusion.replace(/\n/g, '<br>')}</p>
    `;

    if (report.sources && report.sources.length > 0) {
      html += `<h2>${lang === 'uk' ? '6. ДЖЕРЕЛА ТА ВЕРИФІКАЦІЯ' : '6. VERIFIED BIBLIOGRAPHY & SOURCES'}</h2><ol>`;
      report.sources.forEach(src => {
        html += `
          <li style="margin-bottom: 8px;">
            <strong>${src.title}</strong><br>
            <span style="font-size: 9pt; color: #6b7280;">
              Domain: <em>${src.domain}</em> | URL: <a class="source-link" href="${src.url}">${src.url}</a>
            </span>
          </li>
        `;
      });
      html += `</ol>`;
    }

    html += `
      </body>
      </html>
    `;

    // Add Byte Order Mark to assist Word with UTF-8 encoding detection
    const blob = new Blob(['\ufeff' + html], { type: 'application/msword;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `deep-research-${session.id}.doc`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleGenerateAudioBriefing = async () => {
    setAudioLoading(true);
    try {
      let fullContent = `${report.title}\n\n${report.executiveSummary}\n\n`;
      report.sections.forEach(s => { fullContent += `${s.title}\n${s.content}\n`; });
      const res = await fetch("/api/audio-briefing", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: report.title, content: fullContent })
      });
      const data = await res.json();
      if (data.success) {
        setAudioModalData(data.briefing);
      } else {
        alert("Помилка генерації подкасту: " + data.error);
      }
    } catch (err) {
      alert("Помилка підключення до аудіо-сервісу");
    } finally {
      setAudioLoading(false);
    }
  };

  const handleSendTelegram = async () => {
    setTelegramSending(true);
    try {
      let markdown = `# ${report.title}\n\n${report.executiveSummary}\n\n`;
      report.sections.forEach(s => { markdown += `## ${s.title}\n\n${s.content}\n\n`; });
      const res = await fetch("/api/telegram-companion/send-report", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reportTitle: report.title, fileContent: markdown, format: "md" })
      });
      const data = await res.json();
      if (data.success) {
        alert("✅ Звіт успішно відправлено у ваш Telegram-чат!");
      } else {
        alert("Помилка відправки: " + (data.error || "перевірте налаштування"));
      }
    } catch (err) {
      alert("Помилка мережі при відправці у Telegram");
    } finally {
      setTelegramSending(false);
    }
  };

  const handleRunDebate = async () => {
    setDebateLoading(true);
    try {
      let markdown = `${report.title}\n${report.executiveSummary}`;
      const res = await fetch("/api/multi-agent/debate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ topic: report.title, draftContent: markdown })
      });
      const data = await res.json();
      if (data.success) {
        setDebateModalData(data.debate);
      } else {
        alert("Помилка ШІ-Колегії");
      }
    } catch (err) {
      alert("Помилка мережі при запуску ШІ-Колегії");
    } finally {
      setDebateLoading(false);
    }
  };

  const handleSaveGoldStandard = async () => {
    setSavingGold(true);
    try {
      const res = await fetch("/api/learning/save-gold-standard", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          agentId: (session as any).agentId || "deep-research",
          inputPayload: session.query,
          goldOutput: getFullMarkdownReport(),
          userNotes: "Збережено як корпоративний еталон якості",
          modelUsed: (session as any).modelUsed || "smart-hybrid"
        })
      });
      if (res.ok) {
        setGoldSaved(true);
        setTimeout(() => setGoldSaved(false), 5000);
      } else {
        const d = await res.json();
        alert(d.error || "Не вдалося зберегти зразок.");
      }
    } catch (err) {
      alert("Помилка мережі при збереженні еталону.");
    } finally {
      setSavingGold(false);
    }
  };

  const getFullMarkdownReport = () => {
    let markdown = `# ${report.title}\n\n`;
    markdown += `## Executive Summary / Анотація\n\n${report.executiveSummary}\n\n`;
    
    report.sections.forEach(sec => {
      markdown += `## ${sec.title}\n\n${sec.content}\n\n`;
      sec.subsections?.forEach(subsec => {
        markdown += `### ${subsec.title}\n\n${subsec.content}\n\n`;
      });
    });

    markdown += `## Key Metrics & Data Points / Ключові показники\n\n`;
    report.dataPoints.forEach(dp => {
      markdown += `- **${dp.metric}**: ${dp.value} — ${dp.context}\n`;
    });
    markdown += `\n`;

    if (report.swotAnalysis) {
      markdown += `## SWOT Analysis / SWOT Аналіз\n\n`;
      markdown += `### Strengths / Сильні сторони\n${report.swotAnalysis.strengths.map(s => `- ${s}`).join('\n')}\n\n`;
      markdown += `### Weaknesses / Слабкі сторони\n${report.swotAnalysis.weaknesses.map(w => `- ${w}`).join('\n')}\n\n`;
      markdown += `### Opportunities / Можливості\n${report.swotAnalysis.opportunities.map(o => `- ${o}`).join('\n')}\n\n`;
      markdown += `### Threats / Загрози\n${report.swotAnalysis.threats.map(t => `- ${t}`).join('\n')}\n\n`;
    }

    markdown += `## Conclusion / Висновок\n\n${report.conclusion}\n\n`;
    markdown += `## Sources & Grounding / Список джерел\n\n`;
    report.sources.forEach(src => {
      markdown += `- [${src.title}](${src.url}) (${src.domain})\n`;
    });
    return markdown;
  };

  const handlePrint = () => {
    window.print();
  };

  // Find max value in statistical charts to scale the SVG bars properly
  const chartValues = report.charts?.map(c => c.value) || [1];
  const maxChartValue = Math.max(...chartValues, 1);

  return (
    <div id="report-view" className="w-full bg-white border border-slate-200 rounded-lg overflow-hidden relative print:border-none print:shadow-none shadow-xs">
      {/* Top Banner with Action Commands */}
      <div className="bg-slate-50 px-4 py-3 border-b border-slate-200 flex flex-wrap items-center justify-between gap-4 print:hidden">
        <div className="flex items-center gap-3">
          <button
            onClick={onBackToHub}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white hover:bg-slate-100 text-[10px] font-bold font-mono text-slate-700 rounded border border-slate-200 transition-all cursor-pointer uppercase shadow-2xs"
          >
            <ArrowLeft className="w-3.5 h-3.5 text-red-500" />
            <span>{lang === 'uk' ? 'На головну' : 'Hub'}</span>
          </button>
          <div className="h-4 w-px bg-slate-200" />
          <FileText className="w-4 h-4 text-red-500" />
          <span className="text-[10px] font-bold font-mono text-slate-500 uppercase tracking-widest hidden sm:inline">
            {lang === 'uk' ? 'СИНТЕЗОВАНИЙ_АНАЛІТИЧНИЙ_ЗВІТ' : 'SYNTHESIZED_ANALYTIC_REPORT'}
          </span>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* 2026 INNOVATION BUTTONS */}
          <button
            onClick={handleGenerateAudioBriefing}
            disabled={audioLoading}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold font-mono text-pink-600 bg-pink-50 hover:bg-pink-100 rounded border border-pink-200 transition-colors cursor-pointer uppercase"
          >
            {audioLoading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Mic className="w-3.5 h-3.5 text-pink-600" />}
            <span>{lang === 'uk' ? '🎙️ ПОДКАСТ' : '🎙️ PODCAST'}</span>
          </button>

          <TelegramExportButton
            title={report.title || 'Дослідження Deep Research'}
            content={getFullMarkdownReport()}
            agentName="Deep Research"
            agentId="deep-research"
            isUk={lang === 'uk'}
            variant="compact"
          />

          <ExcelExportButton
            title={report.title || 'Таблиці Дослідження'}
            content={getFullMarkdownReport()}
            fileName={`deep_research_${session.id || 'analysis'}`}
            isUk={lang === 'uk'}
            variant="compact"
          />

          <button
            onClick={handleRunDebate}
            disabled={debateLoading}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold font-mono text-purple-600 bg-purple-50 hover:bg-purple-100 rounded border border-purple-200 transition-colors cursor-pointer uppercase"
          >
            {debateLoading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Users className="w-3.5 h-3.5 text-purple-600" />}
            <span>{lang === 'uk' ? '👥 ШІ_КОЛЕГІЯ' : '👥 DEBATE'}</span>
          </button>

          <button
            onClick={() => setShowSignModal(true)}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold font-mono text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded border border-emerald-300 transition-colors cursor-pointer uppercase shadow-xs"
            title="Накласти КЕП / Дія.Підпис"
          >
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
            <span>{lang === 'uk' ? '✍️ ПІДПИСАТИ КЕП' : '✍️ SIGN E-QES'}</span>
          </button>

          <button
            onClick={handleSaveGoldStandard}
            disabled={savingGold}
            className={`inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold font-mono rounded border transition-all cursor-pointer uppercase shadow-xs ${
              goldSaved
                ? 'bg-amber-500 text-white border-amber-600'
                : 'text-amber-700 bg-amber-50 hover:bg-amber-100 border-amber-300'
            }`}
            title="Зберегти цей звіт як корпоративний еталон для автоматичного донавчання моделі"
          >
            {savingGold ? (
              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
            ) : goldSaved ? (
              <Check className="w-3.5 h-3.5 text-white" />
            ) : (
              <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-400" />
            )}
            <span>{goldSaved ? (lang === 'uk' ? '⭐ ЕТАЛОН_ЗБЕРЕЖЕНО!' : '⭐ SAVED!') : (lang === 'uk' ? '⭐ ЗБЕРЕГТИ_ЕТАЛОН' : '⭐ GOLD_STANDARD')}</span>
          </button>

          <div className="h-4 w-px bg-slate-300 hidden md:block" />

          <button
            onClick={handleCopyMarkdown}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold font-mono text-slate-700 hover:bg-slate-100 rounded transition-colors cursor-pointer uppercase"
          >
            {copied ? (
              <>
                <Check className="w-3.5 h-3.5 text-red-500" />
                <span className="hidden sm:inline">{lang === 'uk' ? 'КОПІЮВАННЯ_УСПІШНЕ' : 'COPY_SUCCESS'}</span>
              </>
            ) : (
              <>
                <Copy className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">{lang === 'uk' ? 'КОПІЮВАТИ_MD' : 'COPY_MD'}</span>
              </>
            )}
          </button>

          <button
            onClick={handleDownloadFile}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold font-mono text-slate-700 hover:bg-slate-100 rounded transition-colors cursor-pointer uppercase"
          >
            <Download className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{lang === 'uk' ? 'ЗБЕРЕГТИ_MD' : 'SAVE_MD'}</span>
          </button>

          <button
            onClick={handleDownloadWord}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold font-mono text-red-500 hover:bg-red-50 rounded border border-red-200 transition-colors cursor-pointer uppercase"
          >
            <FileText className="w-3.5 h-3.5 text-red-500" />
            <span className="hidden sm:inline">{lang === 'uk' ? 'СКАЧАТИ_WORD' : 'DOWNLOAD_WORD'}</span>
          </button>

          <button
            onClick={handlePrint}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold font-mono text-slate-700 hover:bg-slate-100 rounded transition-colors cursor-pointer uppercase"
          >
            <Printer className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{lang === 'uk' ? 'ДРУК_PDF' : 'PRINT_PDF'}</span>
          </button>

          <button
            onClick={onNewResearch}
            className="px-3 py-1 text-[10px] font-bold font-mono text-white bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 rounded shadow-xs transition-colors cursor-pointer uppercase"
          >
            <span className="sm:hidden">{lang === 'uk' ? 'НОВИЙ' : 'NEW'}</span>
            <span className="hidden sm:inline">{lang === 'uk' ? 'НОВИЙ_ЗАПИТ' : 'NEW_PROBE'}</span>
          </button>
        </div>
      </div>

      {/* Main Document Body */}
      <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-6 print:p-0">
        {/* Title Header */}
        <div className="space-y-2 pb-4 border-b border-slate-200">
          <h1 className="text-xl sm:text-2xl font-bold font-mono text-slate-800 leading-snug uppercase tracking-tight">
            {report.title}
          </h1>
          <div className="flex flex-wrap items-center gap-3 text-[10px] text-slate-400 font-mono">
            <span className="px-1.5 py-0.5 bg-red-50 text-red-500 border border-red-200 font-bold rounded">
              DEEP_RESEARCH_MAX
            </span>
            <span>•</span>
            <span>{new Date(session.createdAt).toLocaleDateString()}</span>
            <span>•</span>
            <span>
              {lang === 'uk' ? `СЕКТОР: ${session.options.focus.toUpperCase()}` : `SECTOR: ${session.options.focus.toUpperCase()}`}
            </span>
            <span>•</span>
            <span>
              {lang === 'uk' ? `ДЖЕРЕЛА: ${report.sources.length}` : `SOURCES: ${report.sources.length}`}
            </span>
          </div>
        </div>

        {/* View Tabs */}
        <div className="flex border-b border-slate-200 gap-1 overflow-x-auto print:hidden font-mono text-[10px] font-bold">
          <button
            onClick={() => setActiveTab('content')}
            className={`py-2 px-3 border-b-2 transition-all cursor-pointer whitespace-nowrap uppercase ${
              activeTab === 'content'
                ? 'border-red-500 text-red-500'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            {lang === 'uk' ? '01. ТЕКСТ_ЗВІТУ' : '01. ANALYTICAL_NARRATIVE'}
          </button>
          <button
            onClick={() => setActiveTab('data')}
            className={`py-2 px-3 border-b-2 transition-all cursor-pointer whitespace-nowrap uppercase ${
              activeTab === 'data'
                ? 'border-red-500 text-red-500'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            {lang === 'uk' ? '02. КЛЮЧОВІ_МЕТРИКИ' : '02. KEY_METRICS'}
          </button>
          {report.swotAnalysis && (
            <button
              onClick={() => setActiveTab('swot')}
              className={`py-2 px-3 border-b-2 transition-all cursor-pointer whitespace-nowrap uppercase ${
                activeTab === 'swot'
                  ? 'border-red-500 text-red-500'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              {lang === 'uk' ? '03. SWOT_АНАЛІЗ' : '03. SWOT_ANALYSIS'}
            </button>
          )}
          {report.charts && report.charts.length > 0 && (
            <button
              onClick={() => setActiveTab('charts')}
              className={`py-2 px-3 border-b-2 transition-all cursor-pointer whitespace-nowrap uppercase ${
                activeTab === 'charts'
                  ? 'border-red-500 text-red-500'
                  : 'border-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              {lang === 'uk' ? '04. ДІАГРАМИ_ДАНИХ' : '04. DATA_CHARTS'}
            </button>
          )}
        </div>

        {/* Tab content containers */}
        <div className="space-y-8">
          {/* TAB 1: CONTENT */}
          <div className={`${activeTab === 'content' ? 'block' : 'hidden'} print:block space-y-6`}>
            {/* Executive Summary Card */}
            <div className="bg-slate-50 border border-slate-200 rounded p-4 space-y-2 shadow-xs">
              <h3 className="text-xs font-bold font-mono text-red-500 flex items-center gap-1.5 uppercase">
                <TrendingUp className="w-4 h-4" />
                {lang === 'uk' ? 'АНОТАЦІЯ / EXECUTIVE SUMMARY' : 'EXECUTIVE_SUMMARY'}
              </h3>
              <p className="text-[11px] text-slate-700 font-mono leading-relaxed whitespace-pre-line">
                {report.executiveSummary}
              </p>
            </div>

            {/* Document Sections */}
            <div className="space-y-6">
              {report.sections.map((section, sIdx) => (
                <div key={sIdx} className="space-y-2 border-l border-slate-200 pl-4">
                  <h2 className="text-xs font-bold font-mono text-slate-800 flex items-center gap-2 uppercase tracking-wide">
                    <span className="text-[10px] text-red-500 bg-red-50 border border-red-200 w-5 h-5 rounded flex items-center justify-center font-bold">
                      {sIdx + 1}
                    </span>
                    {section.title}
                  </h2>
                  
                  {/* Markdown Narrative text */}
                  <div className="text-[11px] text-slate-700 font-mono leading-relaxed space-y-3 whitespace-pre-line">
                    {section.content}
                  </div>

                  {/* Optional Subsections */}
                  {section.subsections && section.subsections.length > 0 && (
                    <div className="pl-4 border-l border-slate-100 space-y-4 mt-3">
                      {section.subsections.map((sub, subIdx) => (
                        <div key={subIdx} className="space-y-1">
                          <h4 className="text-[11px] font-bold font-mono text-slate-800 uppercase tracking-tight">
                            {sub.title}
                          </h4>
                          <p className="text-[10px] text-slate-500 font-mono leading-relaxed whitespace-pre-line">
                            {sub.content}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* TAB 2: DATA POINTS */}
          <div className={`${activeTab === 'data' ? 'block' : 'hidden'} print:block space-y-4`}>
            <div className="border-b border-slate-200 pb-2 print:hidden font-mono">
              <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                {lang === 'uk' ? 'КЛЮЧОВІ_ОТРИМАНІ_МЕТРИКИ' : 'KEY_EXTRACTED_STATISTICAL_METRICS'}
              </h3>
              <p className="text-[10px] text-slate-400">
                {lang === 'uk' ? 'Підтверджені числові індикатори, виявлені нейромережею:' : 'Verified numerical indicators extracted during live search sequence:'}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 font-mono">
              {report.dataPoints.map((dp, idx) => (
                <div key={idx} className="p-4 rounded border border-slate-200 bg-slate-50">
                  <span className="text-xl font-bold text-red-500">
                    {dp.value}
                  </span>
                  <h4 className="text-[11px] font-bold text-slate-800 mt-1 uppercase tracking-tight">
                    {dp.metric}
                  </h4>
                  <p className="text-[10px] text-slate-400 mt-1 leading-relaxed">
                    {dp.context}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* TAB 3: SWOT ANALYSIS */}
          {report.swotAnalysis && (
            <div className={`${activeTab === 'swot' ? 'block' : 'hidden'} print:block space-y-4 font-mono`}>
              <div className="border-b border-slate-200 pb-2 print:hidden">
                <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                  SWOT_STRATEGIC_MATRIX
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* STRENGTHS */}
                <div className="p-4 rounded border border-emerald-200 bg-emerald-50/50 space-y-2">
                  <div className="flex items-center gap-1.5 text-emerald-600">
                    <ShieldCheck className="w-4 h-4" />
                    <h4 className="text-[10px] font-bold uppercase tracking-wider">{lang === 'uk' ? 'Сильні сторони (Strengths)' : 'Strengths'}</h4>
                  </div>
                  <ul className="space-y-1">
                    {report.swotAnalysis.strengths.map((str, i) => (
                      <li key={i} className="text-[10px] text-slate-700 pl-3 relative before:content-[''] before:absolute before:left-0 before:top-1.5 before:w-1 before:h-1 before:bg-emerald-500 before:rounded-full leading-relaxed">
                        {str}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* WEAKNESSES */}
                <div className="p-4 rounded border border-red-200 bg-red-50/50 space-y-2">
                  <div className="flex items-center gap-1.5 text-red-600">
                    <ShieldAlert className="w-4 h-4" />
                    <h4 className="text-[10px] font-bold uppercase tracking-wider">{lang === 'uk' ? 'Слабкі сторони (Weaknesses)' : 'Weaknesses'}</h4>
                  </div>
                  <ul className="space-y-1">
                    {report.swotAnalysis.weaknesses.map((weak, i) => (
                      <li key={i} className="text-[10px] text-slate-700 pl-3 relative before:content-[''] before:absolute before:left-0 before:top-1.5 before:w-1 before:h-1 before:bg-red-500 before:rounded-full leading-relaxed">
                        {weak}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* OPPORTUNITIES */}
                <div className="p-4 rounded border border-blue-200 bg-blue-50/50 space-y-2">
                  <div className="flex items-center gap-1.5 text-blue-600">
                    <Lightbulb className="w-4 h-4" />
                    <h4 className="text-[10px] font-bold uppercase tracking-wider">{lang === 'uk' ? 'Можливості (Opportunities)' : 'Opportunities'}</h4>
                  </div>
                  <ul className="space-y-1">
                    {report.swotAnalysis.opportunities.map((opp, i) => (
                      <li key={i} className="text-[10px] text-slate-700 pl-3 relative before:content-[''] before:absolute before:left-0 before:top-1.5 before:w-1 before:h-1 before:bg-blue-500 before:rounded-full leading-relaxed">
                        {opp}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* THREATS */}
                <div className="p-4 rounded border border-amber-200 bg-amber-50/50 space-y-2">
                  <div className="flex items-center gap-1.5 text-amber-600">
                    <AlertTriangle className="w-4 h-4" />
                    <h4 className="text-[10px] font-bold uppercase tracking-wider">{lang === 'uk' ? 'Загрози (Threats)' : 'Threats'}</h4>
                  </div>
                  <ul className="space-y-1">
                    {report.swotAnalysis.threats.map((thr, i) => (
                      <li key={i} className="text-[10px] text-slate-700 pl-3 relative before:content-[''] before:absolute before:left-0 before:top-1.5 before:w-1 before:h-1 before:bg-amber-500 before:rounded-full leading-relaxed">
                        {thr}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* TAB 4: CHARTS */}
          {report.charts && report.charts.length > 0 && (
            <div className={`${activeTab === 'charts' ? 'block' : 'hidden'} print:block space-y-4 font-mono`}>
              <div className="border-b border-slate-200 pb-2 print:hidden">
                <h3 className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                  <BarChart2 className="w-4 h-4 text-red-500" />
                  {lang === 'uk' ? 'АНАЛІТИЧНА_ВІЗУАЛІЗАЦІЯ' : 'ANALYTICAL_DATA_VIZ'}
                </h3>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded p-4 space-y-4 shadow-xs">
                {/* Horizontal Stat Bar Chart */}
                <div className="space-y-4">
                  {report.charts.map((item, idx) => {
                    const percentage = (item.value / maxChartValue) * 100;
                    return (
                      <div key={idx} className="space-y-1">
                        <div className="flex justify-between items-center text-[10px] font-bold">
                          <span className="text-slate-700">
                            {item.label}
                          </span>
                          <span className="text-red-500">
                            {item.value} {item.unit || ''}
                          </span>
                        </div>
                        <div className="w-full h-2 bg-slate-100 rounded overflow-hidden relative border border-slate-200">
                          <div
                            className="h-full bg-red-500 rounded transition-all duration-1000 ease-out shadow-xs"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Conclusion */}
          <div className="pt-4 border-t border-slate-200 space-y-2 font-mono">
            <h3 className="text-xs font-bold text-slate-800 flex items-center gap-1.5 uppercase">
              <Info className="w-4 h-4 text-red-500" />
              {lang === 'uk' ? 'ВИСНОВКИ_ТА_РЕКОМЕНДАЦІЇ' : 'CONCLUSION_PROSPECTUS'}
            </h3>
            <p className="text-[10px] text-slate-700 leading-relaxed whitespace-pre-line bg-slate-50 p-3 rounded border border-slate-200">
              {report.conclusion}
            </p>
          </div>

          {/* Sources / Bibliography */}
          {report.sources && report.sources.length > 0 && (
            <div className="pt-4 border-t border-slate-200 space-y-3 print:break-inside-avoid font-mono">
              <h3 className="text-[10px] font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                <BookOpen className="w-4 h-4 text-red-500" />
                {lang === 'uk' ? 'СПИСОК_ДЖЕРЕЛ_ТА_ОБҐРУНТУВАННЯ' : 'VERIFIED_SOURCES_BIBLIOGRAPHY'}
              </h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {report.sources.map((src, idx) => (
                  <a
                    key={idx}
                    href={src.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2.5 bg-slate-50 border border-slate-200 hover:border-red-500/30 rounded transition-all flex items-start gap-2.5 group shadow-xs"
                  >
                    <span className="text-[9px] font-bold text-slate-600 bg-slate-100 w-4 h-4 rounded flex items-center justify-center border border-slate-200 shrink-0">
                      {idx + 1}
                    </span>
                    <div className="space-y-0.5 min-w-0 flex-1">
                      <p className="text-[10px] font-bold text-slate-700 line-clamp-1 group-hover:text-red-500 transition-colors">
                        {src.title}
                      </p>
                      <div className="flex items-center gap-1.5 text-[8px] text-slate-400">
                        <span className="font-bold text-red-500">{src.domain}</span>
                        <span>•</span>
                        <span className="truncate max-w-[120px]">{src.url}</span>
                      </div>
                    </div>
                    <ExternalLink className="w-3 h-3 text-slate-400 group-hover:text-red-500 shrink-0 transition-colors mt-0.5" />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* AUDIO PODCAST MODAL OVERLAY */}
      {audioModalData && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl max-w-xl w-full p-6 text-slate-100 shadow-2xl relative">
            <button
              onClick={() => setAudioModalData(null)}
              className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white rounded-lg bg-slate-800"
            >
              <X className="w-5 h-5" />
            </button>

            <h3 className="text-lg font-bold text-pink-400 mb-2 flex items-center gap-2">
              <Radio className="w-5 h-5" /> {audioModalData.podcastTitle || "ШІ-Подкаст за звітом"}
            </h3>
            <p className="text-xs text-slate-300 mb-4">{audioModalData.summary}</p>

            <div className="space-y-2 max-h-72 overflow-y-auto mb-4 p-3 bg-slate-950 rounded-xl border border-slate-800">
              {audioModalData.dialogue?.map((item: any, idx: number) => (
                <div key={idx} className="text-xs p-2 rounded-lg bg-slate-900 border border-slate-800">
                  <span className={`font-bold ${item.speaker === "Олексій" ? "text-indigo-400" : "text-purple-400"}`}>
                    {item.speaker}:
                  </span>{" "}
                  <span className="text-slate-300">{item.text}</span>
                </div>
              ))}
            </div>

            <button
              onClick={() => setAudioModalData(null)}
              className="w-full py-2.5 bg-pink-600 hover:bg-pink-500 text-white rounded-xl text-xs font-bold transition-all"
            >
              Закрити подкаст
            </button>
          </div>
        </div>
      )}

      {/* DEBATE MODAL OVERLAY */}
      {debateModalData && (
        <div className="fixed inset-0 bg-slate-950/80 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl max-w-2xl w-full p-6 text-slate-100 shadow-2xl relative">
            <button
              onClick={() => setDebateModalData(null)}
              className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white rounded-lg bg-slate-800"
            >
              <X className="w-5 h-5" />
            </button>

            <h3 className="text-lg font-bold text-purple-400 mb-2 flex items-center gap-2">
              <Users className="w-5 h-5" /> Результати Аудиту ШІ-Колегії
            </h3>
            <p className="text-xs text-emerald-400 font-semibold mb-4 bg-emerald-950/60 p-2.5 rounded-lg border border-emerald-800/40">
              🤝 {debateModalData.finalConsensus}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              {debateModalData.stages?.map((st: any, idx: number) => (
                <div key={idx} className="p-3 bg-slate-950 border border-slate-800 rounded-xl">
                  <span className="text-xs text-purple-300 font-bold block">{st.agent}</span>
                  <span className="text-[9px] text-slate-500 block mb-1">{st.role}</span>
                  <p className="text-xs text-slate-300">{st.findings}</p>
                </div>
              ))}
            </div>

            <button
              onClick={() => setDebateModalData(null)}
              className="w-full py-2.5 bg-purple-600 hover:bg-purple-500 text-white rounded-xl text-xs font-bold transition-all"
            >
              Закрити рецензію ШІ-Колегії
            </button>
          </div>
        </div>
      )}

      {/* DIGITAL SIGNATURE MODAL */}
      <DigitalSignatureModal
        isOpen={showSignModal}
        onClose={() => setShowSignModal(false)}
        documentTitle={report.title || 'Аналітичний звіт Deep Research'}
        documentContent={getFullMarkdownReport()}
        onSignatureComplete={(stampText) => {
          setSignedStampText(stampText);
        }}
      />
    </div>
  );
}
